import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, of, Subject, catchError, tap, map, firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { UserStateService } from './user-state.service';
import { TranslateService } from '@ngx-translate/core';
import { UploadInfo, UploadStatus, PhotoInfo, PhotoUpload as PhotoUploadModel, PhotoUploadStatus, getFileTypeId, PhotoImportType } from '../models';
import SparkMD5 from 'spark-md5';

/**
 * Upload Service - converted from UploadDelegate.as and UploadController.as
 * 
 * AMFphp Service: UploadService
 * Methods:
 * - checkPhotoBeforeUpload(projectId, allSizes, md5, user) -> 'PHOTO_DOES_NOT_EXIST' or PhotoInfo
 * - checkThemePhotoBeforeUpload(projectId, md5, photoType, user)
 * 
 * Upload Flow:
 * 1. Compute MD5 hash of file
 * 2. Call checkPhotoBeforeUpload to see if photo already exists
 * 3. If 'PHOTO_DOES_NOT_EXIST', POST to /UploadPhoto.php
 * 4. Response contains PhotoInfo with serverId, serverName, pathStructureId, etc.
 */

export interface UploadProgress {
  id: string;
  fileName: string;
  progress: number;
  status: UploadStatus;
  error?: string;
}

export interface PhotoUploadParams {
  projectId: number;
  allSizes?: boolean;
  md5: string;
  photoType?: number;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private readonly api = inject(ApiService);
  private readonly userState = inject(UserStateService);
  private readonly translate = inject(TranslateService);

  private readonly chunkUploadThresholdBytes = 5 * 1024 * 1024;
  private readonly uploadChunkSizeBytes = 2 * 1024 * 1024;

  private readonly _uploads = signal<UploadProgress[]>([]);
  private readonly _currentUpload = signal<UploadProgress | null>(null);

  readonly uploads = this._uploads.asReadonly();
  readonly currentUpload = this._currentUpload.asReadonly();

  readonly isUploading = computed(() => {
    return this._uploads().some(u => u.status === UploadStatus.UPLOADING);
  });

  readonly totalProgress = computed(() => {
    const uploads = this._uploads().filter(u => 
      u.status === UploadStatus.UPLOADING || u.status === UploadStatus.COMPLETED
    );
    if (uploads.length === 0) return 0;
    return uploads.reduce((sum, u) => sum + u.progress, 0) / uploads.length;
  });

  readonly pendingCount = computed(() => {
    return this._uploads().filter(u => u.status === UploadStatus.PENDING).length;
  });

  readonly completedCount = computed(() => {
    return this._uploads().filter(u => u.status === UploadStatus.COMPLETED).length;
  });

  private uploadComplete$ = new Subject<{ id: string; response: any }>();

  uploadFile(file: File, productId?: number, projectId?: number): Observable<any> {
    const user = this.userState.user();
    if (!user) return of(null);

    const uploadId = this.generateUploadId();
    const formData = new FormData();
    formData.append('file', file);
    if (productId) formData.append('productId', productId.toString());
    if (projectId) formData.append('projectId', projectId.toString());

    // Add to queue
    this.addUpload({
      id: uploadId,
      fileName: file.name,
      progress: 0,
      status: UploadStatus.PENDING
    });

    return this.api.upload<any>('/upload/files', formData, {
      onProgress: (progress) => {
        this.updateUpload(uploadId, { progress, status: UploadStatus.UPLOADING });
      }
    }).pipe(
      tap(response => {
        this.updateUpload(uploadId, { progress: 100, status: UploadStatus.COMPLETED });
        this.uploadComplete$.next({ id: uploadId, response });
      }),
      catchError(error => {
        this.updateUpload(uploadId, {
          status: UploadStatus.ERROR,
          error: this.translate.instant('UPLOADS.ERROR.UPLOAD_FAILED')
        });
        return of(null);
      })
    );
  }

  uploadMultipleFiles(files: File[], productId?: number, projectId?: number): void {
    for (const file of files) {
      this.uploadFile(file, productId, projectId).subscribe();
    }
  }

  cancelUpload(uploadId: string): void {
    this.updateUpload(uploadId, { status: UploadStatus.CANCELLED });
  }

  cancel(uploadId: number | string): void {
    this.cancelUpload(uploadId.toString());
  }

  removeUpload(uploadId: string): void {
    const uploads = this._uploads().filter(u => u.id !== uploadId);
    this._uploads.set(uploads);
  }

  clearCompleted(): void {
    const uploads = this._uploads().filter(u => 
      u.status !== UploadStatus.COMPLETED && u.status !== UploadStatus.CANCELLED
    );
    this._uploads.set(uploads);
  }

  clearAll(): void {
    this._uploads.set([]);
  }

  getUploadedFiles(projectId?: number): Observable<any[]> {
    const user = this.userState.user();
    if (!user) return of([]);

    const params: Record<string, string> = {};
    if (projectId) params['projectId'] = projectId.toString();

    return this.api.get<any[]>('/uploads', { params }).pipe(
      catchError(() => of([]))
    );
  }

  deleteUploadedFile(fileId: number): Observable<boolean> {
    const user = this.userState.user();
    if (!user) return of(false);

    return this.api.delete<boolean>('/uploads/' + fileId).pipe(
      catchError(() => of(false))
    );
  }

  /**
   * Check photo before upload
   * AMF: UploadService.checkPhotoBeforeUpload(projectId, allSizes, md5, user)
   * @returns 'PHOTO_DOES_NOT_EXIST' or PhotoInfo object
   */
  checkPhotoBeforeUpload(params: PhotoUploadParams): Observable<string | PhotoInfo> {
    const user = this.userState.user();
    if (!user) return of('PHOTO_DOES_NOT_EXIST');

    const allSizes = (params.allSizes || params.projectId > 0) ? 1 : 0;
    console.log('[UploadService] checkPhotoBeforeUpload - projectId:', params.projectId, 'allSizes:', allSizes, 'md5:', params.md5);
    return this.api.get<string | PhotoInfo>('/upload/projects/' + params.projectId + '/photos/' + params.md5 + '/check', { params: { allSizes } }).pipe(
      map(result => {
        console.log('[UploadService] checkPhotoBeforeUpload result:', result);
        return result as string | PhotoInfo;
      }),
      catchError(err => {
        console.error('[UploadService] checkPhotoBeforeUpload error:', err);
        return of('PHOTO_DOES_NOT_EXIST' as string);
      })
    );
  }

  /**
   * Check if a low-res photo already exists on Hetzner storage.
   * Separate from the production checkPhotoBeforeUpload — standalone PHP endpoint.
   * POST to /CheckPhotoLowRes.php with md5, customerId, credentials
   * @returns 'PHOTO_DOES_NOT_EXIST' or PhotoInfo object
   */
  checkPhotoLowRes(md5: string): Observable<string | PhotoInfo> {
    const user = this.userState.user();
    if (!user) return of('PHOTO_DOES_NOT_EXIST');

    return this.api.post<string | PhotoInfo>('/photos-low-res/check', { md5 }).pipe(
      map(result => {
        if (!result || result === 'PHOTO_DOES_NOT_EXIST') {
          return 'PHOTO_DOES_NOT_EXIST' as string;
        }
        console.log('[UploadService] checkPhotoLowRes: photo exists on Hetzner:', result);
        return result as PhotoInfo;
      }),
      catchError(err => {
        console.error('[UploadService] checkPhotoLowRes error:', err);
        return of('PHOTO_DOES_NOT_EXIST' as string);
      })
    );
  }

  /**
   * Check theme photo before upload
   * AMF: UploadService.checkThemePhotoBeforeUpload(projectId, md5, photoType, user)
   */
  checkThemePhotoBeforeUpload(params: PhotoUploadParams): Observable<any> {
    const user = this.userState.user();
    if (!user) return of(null);

    return this.api.get<any>('/upload/themes/' + params.projectId + '/photos/' + params.md5 + '/check', { params: { photoType: params.photoType ?? 0 } }).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Upload photo to server
   * POST to /UploadPhoto.php with multipart/form-data
   * @returns PhotoInfo from server response, or 'OK' if upload succeeded but needs follow-up check
   */
  uploadPhotoToServer(
    file: File,
    md5: string,
    options: {
      projectId?: number;
      allSizes?: boolean;
      lowRes?: boolean;
      fileTypeId?: number;
      photoType?: number;
      onProgress?: (progress: number) => void;
    } = {}
  ): Observable<PhotoInfo | 'OK' | null> {
    const user = this.userState.user();
    if (!user) return of(null);

    const path = options.lowRes ? '/photos-low-res/upload' : '/upload/photos';

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('name', file.name);
    formData.append('type', String(options.fileTypeId ?? getFileTypeId(file.name)));
    formData.append('photoType', String(options.photoType ?? PhotoImportType.PHOTO));
    formData.append('md5', md5);
    formData.append('creationDate', this.formatDate(new Date()));
    formData.append('projectId', String(options.projectId ?? 0));
    formData.append('allSizes', (options.allSizes || (options.projectId && options.projectId > 0)) ? '1' : '0');

    console.log('[UploadService] Uploading photo via ApiService:', path);
    console.log('[UploadService] Upload params:', {
      name: file.name,
      type: options.fileTypeId ?? getFileTypeId(file.name),
      photoType: options.photoType ?? PhotoImportType.PHOTO,
      md5,
      projectId: options.projectId ?? 0,
      allSizes: options.allSizes ?? false
    });

    return this.api.upload<any>(path, formData, { onProgress: options.onProgress }).pipe(
      map(response => {
        console.log('[UploadService] Upload raw response:', response);
        const parsed = this.parseUploadResponse(response, file.name, md5);
        console.log('[UploadService] Upload parsed response:', parsed);
        return parsed;
      }),
      catchError(err => {
        // Re-throw storage limit errors so they propagate to the caller
        if (err?.message?.includes('ERROR_STORAGE_LIMIT_EXCEEDED')) {
          throw err;
        }
        console.error('[UploadService] Upload error:', err);
        return of(null);
      })
    );
  }

  private parseUploadResponse(rawResponse: any, fileName: string, md5: string): PhotoInfo | 'OK' | null {
    if (rawResponse === 'OK' || rawResponse === 'ok') {
      console.log('[UploadService] Upload successful (OK response)');
      return 'OK';
    }

    // Detect storage limit exceeded error from backend
    if (typeof rawResponse === 'string' && rawResponse.includes('ERROR_STORAGE_LIMIT_EXCEEDED')) {
      throw new Error('ERROR_STORAGE_LIMIT_EXCEEDED');
    }

    let response: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try {
        response = JSON.parse(rawResponse);
      } catch (e) {
        console.warn('[UploadService] Response is not JSON:', rawResponse);
        if (rawResponse && !rawResponse.toLowerCase().includes('error')) {
          return 'OK';
        }
        return null;
      }
    }

    if (response && Array.isArray(response) && response.length > 0) {
      const photoInfo: PhotoInfo = {
        id: response[0].photo_id || 0,
        serverId: response[0].server_id,
        serverName: response[0].server_name,
        pathStructureId: response[0].path_structure_id,
        width: response[0].width,
        height: response[0].height,
        md5: md5,
        name: fileName,
        urlPNG: response[0].urlPNG ?? response[0].url_png ?? undefined,
        sizes: response.map((s: any) => ({
          width: s.width,
          height: s.height,
          name: s.server_name
        }))
      };
      return photoInfo;
    }

    // Handle REST API object response (serverName at top level — accept both
    // camelCase and snake_case; the endpoint's body shape has changed over
    // time and an unrecognized-but-successful body must not read as failure)
    const topServerName = response && typeof response === 'object' && !Array.isArray(response)
      ? (response.serverName || response.server_name)
      : '';
    if (topServerName) {
      const thumbSize = (response.sizes || []).find((s: any) => (s.serverName || s.server_name)?.includes('_t'));
      const photoInfo: PhotoInfo = {
        id: response.id || response.photo_id || 0,
        serverId: response.serverId || response.server_id || 0,
        serverName: topServerName,
        pathStructureId: response.pathStructureId || response.path_structure_id || 0,
        width: parseFloat(response.width) || 0,
        height: parseFloat(response.height) || 0,
        md5: response.md5 || md5,
        name: response.name || fileName,
        url: response.url || '',
        urlPNG: response.urlPNG ?? response.url_png ?? undefined,
        thumbnail: thumbSize?.url || '',
        sizes: (response.sizes || []).map((s: any) => ({
          width: s.width || 0,
          height: s.height || 0,
          name: s.serverName || s.server_name || '',
          url: s.url || ''
        }))
      };
      return photoInfo;
    }

    return null;
  }

  private shouldUseChunkUpload(file: File): boolean {
    return file.size >= this.chunkUploadThresholdBytes;
  }

  private readFileSlice(file: File, start: number, end: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Invalid chunk data'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file chunk'));
      reader.readAsArrayBuffer(file.slice(start, end));
    });
  }

  private computeArrayBufferMd5(buffer: ArrayBuffer): string {
    const spark = new SparkMD5.ArrayBuffer();
    spark.append(buffer);
    return spark.end();
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const step = 0x8000;
    for (let i = 0; i < bytes.length; i += step) {
      binary += String.fromCharCode(...bytes.subarray(i, i + step));
    }
    return btoa(binary);
  }

  private async uploadPhotoInChunks(
    file: File,
    md5: string,
    options: {
      projectId?: number;
      allSizes?: boolean;
      lowRes?: boolean;
      fileTypeId?: number;
      photoType?: number;
      onProgress?: (progress: number) => void;
    } = {}
  ): Promise<PhotoInfo | 'OK' | null> {
    const user = this.userState.user();
    if (!user) return null;

    const path = options.lowRes ? '/photos-low-res/upload-chunks' : '/upload/photos-chunks';

    const allSizes = (options.allSizes || (options.projectId && options.projectId > 0)) ? 1 : 0;
    const chunkSize = this.uploadChunkSizeBytes;
    const chunks = Math.ceil(file.size / chunkSize);

    console.log('[UploadService] Uploading photo in chunks via ApiService:', path, 'chunks:', chunks);

    for (let index = 0; index < chunks; index++) {
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const buffer = await this.readFileSlice(file, start, end);
      const chunkMd5 = this.computeArrayBufferMd5(buffer);
      const chunkBase64 = this.arrayBufferToBase64(buffer);

      const formData = new FormData();
      formData.append('md5', md5);
      formData.append('name', file.name);
      formData.append('customerId', String(user.id));
      formData.append('projectId', String(options.projectId ?? 0));
      formData.append('type', String(options.fileTypeId ?? getFileTypeId(file.name)));
      formData.append('allSizes', String(allSizes));
      formData.append('fileOffset', String(start));
      formData.append('fileSize', String(file.size));
      formData.append('chunkMd5', chunkMd5);
      formData.append('chunk', chunkBase64);

      const rawResponse = await firstValueFrom(
        this.api.upload<any>(path, formData)
      );

      options.onProgress?.(Math.round(((index + 1) / chunks) * 100));

      // Handle JSON response from new API
      if (rawResponse && typeof rawResponse === 'object') {
        if (rawResponse.error) {
          if (String(rawResponse.error).includes('ERROR_STORAGE_LIMIT_EXCEEDED')) {
            throw new Error('ERROR_STORAGE_LIMIT_EXCEEDED');
          }
          console.error('[UploadService] Chunk upload error:', rawResponse.error);
          return null;
        }
        // Incomplete chunk — skip ahead if server already has later chunks
        if (rawResponse.complete === false) {
          if (typeof rawResponse.offset === 'number' && rawResponse.offset > 0) {
            // Server already has all bytes — nothing left to send
            if (rawResponse.offset >= file.size) {
              console.log(`[UploadService] Server already has all data (offset ${rawResponse.offset}/${file.size})`);
              options.onProgress?.(100);
              return 'OK';
            }
            const serverChunkIndex = Math.floor(rawResponse.offset / chunkSize);
            if (serverChunkIndex > index + 1) {
              console.log(`[UploadService] Server at offset ${rawResponse.offset}, skipping to chunk ${serverChunkIndex}/${chunks}`);
              index = serverChunkIndex - 1; // for-loop will increment to serverChunkIndex
            }
          }
          options.onProgress?.(Math.round(((index + 1) / chunks) * 100));
          continue;
        }
        // Complete — parse the photo from response
        if (rawResponse.complete === true && rawResponse.photo) {
          return this.parseUploadResponse(rawResponse.photo, file.name, md5) ?? this.parseUploadResponse(rawResponse, file.name, md5);
        }
        // Dedup hit — photo already existed
        if (rawResponse.photo) {
          return this.parseUploadResponse(rawResponse.photo, file.name, md5);
        }
        continue;
      }

      // Legacy string response handling
      const trimmed = typeof rawResponse === 'string' ? rawResponse.trim() : '';

      if (trimmed.includes('ERROR_STORAGE_LIMIT_EXCEEDED')) {
        throw new Error('ERROR_STORAGE_LIMIT_EXCEEDED');
      }

      if (trimmed && /^[0-9]+$/.test(trimmed)) {
        continue;
      }

      const parsed = this.parseUploadResponse(rawResponse, file.name, md5);
      if (parsed !== null) {
        return parsed;
      }

      console.error('[UploadService] Chunk upload unexpected response:', rawResponse);
      return null;
    }

    return 'OK';
  }

  /**
   * Complete upload flow: check if exists, upload if not
   * @returns PhotoInfo from either check (if exists) or upload (if new)
   */
  async uploadPhoto(
    file: File,
    md5: string,
    options: {
      projectId?: number;
      allSizes?: boolean;
      lowRes?: boolean;
      fileTypeId?: number;
      photoType?: number;
      forceUpload?: boolean;
      onProgress?: (progress: number) => void;
      onStatusChange?: (status: PhotoUploadStatus) => void;
    } = {}
  ): Promise<PhotoInfo | null> {
    const projectId = options.projectId ?? 0;
    const allSizes = options.allSizes ?? false;
    const lowRes = options.lowRes ?? false;

    // Step 1: Check if photo already exists (skip if forceUpload — file may be missing from storage)
    if (!options.forceUpload) {
      options.onStatusChange?.(PhotoUploadStatus.CHECKING);

      if (lowRes) {
        const checkResult = await firstValueFrom(this.checkPhotoLowRes(md5));
        if (checkResult && typeof checkResult !== 'string') {
          console.log('[UploadService] Photo already exists on Hetzner:', checkResult);
          options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
          return this.mapToPhotoInfo(checkResult, file.name, md5);
        }
      } else {
        const checkResult = await firstValueFrom(
          this.checkPhotoBeforeUpload({ projectId, allSizes, md5 })
        );

        if (checkResult && typeof checkResult !== 'string') {
          console.log('[UploadService] Photo already exists on server:', checkResult);
          options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
          return this.mapToPhotoInfo(checkResult, file.name, md5);
        }

        if (checkResult !== 'PHOTO_DOES_NOT_EXIST') {
          options.onStatusChange?.(PhotoUploadStatus.ERROR);
          return null;
        }
      }
    }

    // Step 2: Upload the photo
    {
      console.log(`[UploadService] Uploading photo${lowRes ? ' (low-res to Hetzner)' : ''}...`);
      options.onStatusChange?.(PhotoUploadStatus.UPLOADING);

      const uploadResult = this.shouldUseChunkUpload(file)
        ? await this.uploadPhotoInChunks(file, md5, {
            projectId,
            allSizes,
            lowRes,
            fileTypeId: options.fileTypeId,
            photoType: options.photoType,
            onProgress: options.onProgress
          })
        : await firstValueFrom(
            this.uploadPhotoToServer(file, md5, {
              projectId,
              allSizes,
              lowRes,
              fileTypeId: options.fileTypeId,
              photoType: options.photoType,
              onProgress: options.onProgress
            })
          );

      // If upload returned PhotoInfo directly, use it
      if (uploadResult && typeof uploadResult === 'object' && 'serverId' in uploadResult && uploadResult.serverId) {
        console.log('[UploadService] Upload returned PhotoInfo:', uploadResult);
        options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
        return uploadResult as PhotoInfo;
      }

      // Anything else — 'OK', an unrecognized body shape, or a lost/errored
      // response — can still be a LANDED upload (the endpoint answers 201
      // with body shapes that changed over time, and 502s can eat the
      // response after the server stored the file). Confirm against the
      // server with backoff (~30s) instead of failing outright; the old code
      // only confirmed the literal 'OK' shape and gave up after 6s, so
      // successful re-uploads were reported as errors.
      console.log('[UploadService] Upload finished without direct photo info (result:', uploadResult, ') — confirming with retry...');
      const delays = [1000, 2000, 4000, 8000, 15000];
      for (const delay of delays) {
        await new Promise(resolve => setTimeout(resolve, delay));

        const photoInfo = await firstValueFrom(
          this.checkPhotoBeforeUpload({ projectId, allSizes, md5 })
        );

        if (photoInfo && typeof photoInfo !== 'string') {
          console.log('[UploadService] Photo info fetched after upload (delay:', delay, 'ms):', photoInfo);
          options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
          return this.mapToPhotoInfo(photoInfo, file.name, md5);
        }
        console.log('[UploadService] Photo not ready yet after', delay, 'ms, retrying...');
      }

      options.onStatusChange?.(PhotoUploadStatus.ERROR);
      console.error('[UploadService] Failed to get photo info after upload');
      return null;
    }
  }

  /**
   * Map server response to PhotoInfo
   */
  private mapToPhotoInfo(response: any, fileName: string, md5: string): PhotoInfo {
    // Map sizes, preserving the url from the API response when available
    const sizes = (response.sizes || []).map((s: any) => ({
      width: s.width,
      height: s.height,
      name: s.serverName || s.name || '',
      url: s.url || ''
    }));

    return {
      id: response.id || response.photo_id || 0,
      serverId: response.serverId || response.server_id,
      serverName: response.serverName || response.server_name || '',
      pathStructureId: response.pathStructureId || response.path_structure_id,
      width: response.width,
      height: response.height,
      md5: md5,
      name: fileName,
      size: response.size,
      importDate: response.importDate || response.import_date,
      url: response.url || '',
      urlPNG: response.urlPNG ?? response.url_png ?? undefined,
      sizes
    };
  }

  /**
   * Compute MD5 hash of a file
   * Uses SparkMD5 library for efficient chunked hashing
   */
  async computeMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const chunkSize = 2097152; // 2MB chunks
      const chunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;
      
      const spark = new SparkMD5.ArrayBuffer();
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target?.result) {
          spark.append(e.target.result as ArrayBuffer);
        }
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          const hash = spark.end();
          console.log('[UploadService] MD5 computed:', hash, 'for file:', file.name);
          resolve(hash);
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file for MD5'));
      };

      const loadNext = () => {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        reader.readAsArrayBuffer(file.slice(start, end));
      };

      loadNext();
    });
  }

  /**
   * Decode a photo file ONCE and produce a thumbnail + natural dimensions.
   *
   * Why this exists: the legacy flow called `generateThumbnail` (which
   * did its own FileReader → base64 data-URL → `<img>` → canvas) and
   * then ran a separate `getLocalImageDimensions` that loaded the SAME
   * file into a SECOND `<img>` just to read naturalWidth/Height. For a
   * 50 MP camera photo that's two full JPEG decodes plus a 67 MB base64
   * string allocation per photo, which dominated the per-photo prep
   * cost during large imports (≈130 photos).
   *
   * The replacement: one `URL.createObjectURL(file)` → one `<img>` load
   * → captures naturalWidth/Height from `img.onload` AND draws the
   * thumb from the same decoded bitmap. No FileReader, no base64. The
   * internal objectUrl is revoked before resolve.
   *
   * `maxSize` caps the longer thumb dimension in pixels (default 400).
   * JPEG quality fixed at 0.7. `width`/`height` in the result are the
   * file's natural pixel size (NOT the thumb dimensions).
   */
  async preparePhotoLocal(
    file: File,
    maxSize: number = 400
  ): Promise<{ thumbnailDataUrl: string; width: number; height: number } | null> {
    if (!file.type.startsWith('image/')) return null;
    return new Promise((resolve) => {
      const decodeUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const naturalW = img.naturalWidth || img.width;
          const naturalH = img.naturalHeight || img.height;

          let w = naturalW;
          let h = naturalH;
          if (w > h) {
            if (w > maxSize) { h = Math.round((h * maxSize) / w); w = maxSize; }
          } else {
            if (h > maxSize) { w = Math.round((w * maxSize) / h); h = maxSize; }
          }

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, w, h);
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve({ thumbnailDataUrl, width: naturalW, height: naturalH });
        } catch {
          resolve(null);
        } finally {
          URL.revokeObjectURL(decodeUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(decodeUrl);
        resolve(null);
      };
      img.src = decodeUrl;
    });
  }

  /**
   * Backwards-compat wrapper: returns just the thumbnail data URL.
   * New callers should prefer `preparePhotoLocal()` which also returns
   * the natural dimensions in the same single decode pass.
   */
  async generateThumbnail(file: File, maxSize: number = 400): Promise<string | null> {
    const prep = await this.preparePhotoLocal(file, maxSize);
    return prep?.thumbnailDataUrl ?? null;
  }

  /**
   * Upload a theme photo (clipart or background)
   * Uses UploadPhotoTheme.php and checkThemePhotoBeforeUpload
   *
   * @param file - File to upload
   * @param md5 - MD5 hash of the file
   * @param options - Upload options including themeSetId and photoType
   * @returns PhotoInfo with sizes array
   */
  async uploadThemePhoto(
    file: File,
    md5: string,
    options: {
      themeSetId: number;
      photoType: number; // 1 = clipart, 4 = background
      onProgress?: (progress: number) => void;
      onStatusChange?: (status: PhotoUploadStatus) => void;
    }
  ): Promise<PhotoInfo | null> {
    const user = this.userState.user();
    if (!user) return null;

    const { themeSetId, photoType } = options;

    // Step 1: Check if photo already exists
    options.onStatusChange?.(PhotoUploadStatus.CHECKING);
    console.log('[UploadService] checkThemePhotoBeforeUpload:', { themeSetId, md5, photoType });

    const checkResult = await firstValueFrom(
      this.checkThemePhotoBeforeUpload({ projectId: themeSetId, md5, photoType })
    );

    console.log('[UploadService] checkThemePhotoBeforeUpload result:', checkResult);

    // If photo already exists, return the PhotoInfo
    if (checkResult && typeof checkResult !== 'string' && checkResult !== 'PHOTO_DOES_NOT_EXIST') {
      console.log('[UploadService] Theme photo already exists on server:', checkResult);
      options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
      return this.mapToPhotoInfo(checkResult, file.name, md5);
    }

    // Step 2: Upload the photo via ApiService
    console.log('[UploadService] Theme photo does not exist, uploading...');
    options.onStatusChange?.(PhotoUploadStatus.UPLOADING);

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('name', file.name);
    formData.append('type', String(getFileTypeId(file.name)));
    formData.append('photoType', String(photoType));
    formData.append('md5', md5);
    formData.append('creationDate', this.formatDate(new Date()));
    formData.append('projectId', String(themeSetId));
    formData.append('allSizes', '1');

    console.log('[UploadService] Uploading theme photo via ApiService');

    try {
      const uploadResponse = await firstValueFrom(
        this.api.upload<any>('/upload/themes/' + themeSetId + '/photos', formData, { onProgress: options.onProgress })
      );

      console.log('[UploadService] Theme upload response:', uploadResponse);

      // If upload returned photo info directly, use it
      if (uploadResponse && typeof uploadResponse === 'object' && uploadResponse.serverId) {
        console.log('[UploadService] Theme upload returned PhotoInfo directly');
        options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
        return this.mapToPhotoInfo(uploadResponse, file.name, md5);
      }

      // Otherwise fetch photo info with retry/backoff (server may need processing time)
      const delays = [500, 1000, 2000];
      for (const delay of delays) {
        await new Promise(resolve => setTimeout(resolve, delay));

        const photoInfo = await firstValueFrom(
          this.checkThemePhotoBeforeUpload({ projectId: themeSetId, md5, photoType })
        );

        if (photoInfo && typeof photoInfo !== 'string' && photoInfo !== 'PHOTO_DOES_NOT_EXIST') {
          console.log('[UploadService] Theme photo info fetched after', delay, 'ms:', photoInfo);
          options.onStatusChange?.(PhotoUploadStatus.COMPLETE);
          return this.mapToPhotoInfo(photoInfo, file.name, md5);
        }
        console.log('[UploadService] Theme photo not ready after', delay, 'ms, retrying...');
      }

      options.onStatusChange?.(PhotoUploadStatus.ERROR);
      console.error('[UploadService] Failed to get theme photo info after upload (all retries exhausted)');
      return null;

    } catch (error) {
      console.error('[UploadService] Theme upload error:', error);
      options.onStatusChange?.(PhotoUploadStatus.ERROR);
      return null;
    }
  }

  /**
   * Replace a missing photo: delete old DB entry, upload as new, replace references.
   * POST to /upload/photos/replace with multipart/form-data
   */
  replacePhoto(
    file: File,
    oldPhotoId: number | string,
    md5: string,
    options: {
      projectId: number;
      onProgress?: (progress: number) => void;
    }
  ): Observable<PhotoInfo | null> {
    const user = this.userState.user();
    if (!user) return of(null);

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('oldPhotoId', String(oldPhotoId));
    formData.append('md5', md5);
    formData.append('name', file.name);
    formData.append('projectId', String(options.projectId));
    formData.append('allSizes', '1');

    console.log('[UploadService] Replacing photo via /upload/photos/replace, oldPhotoId:', oldPhotoId);

    return this.api.upload<any>('/upload/photos/replace', formData, { onProgress: options.onProgress }).pipe(
      map(response => {
        console.log('[UploadService] Replace photo raw response:', response);
        const parsed = this.parseUploadResponse(response, file.name, md5);
        if (parsed && typeof parsed === 'object') {
          return parsed as PhotoInfo;
        }
        return null;
      }),
      catchError(err => {
        console.error('[UploadService] Replace photo error:', err);
        return of(null);
      })
    );
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private addUpload(upload: UploadProgress): void {
    this._uploads.set([...this._uploads(), upload]);
  }

  private updateUpload(id: string, updates: Partial<UploadProgress>): void {
    const uploads = this._uploads().map(u => 
      u.id === id ? { ...u, ...updates } : u
    );
    this._uploads.set(uploads);

    // Update current upload
    const current = uploads.find(u => u.id === id);
    if (current?.status === UploadStatus.UPLOADING) {
      this._currentUpload.set(current);
    } else if (this._currentUpload()?.id === id) {
      this._currentUpload.set(null);
    }
  }

  private generateUploadId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
